"use strict";
window.theme = window.theme || {};
theme.CartTransactions = (function () {
    let busy = false;
    let unresolved = false;
    let lastCart = null;
    let sequence = 0;
    let writeStarted = false;
    const disabled = new Map();
    const stringsNode = document.getElementById('lkc-cart-messages');
    let strings = {};
    try { strings = JSON.parse(stringsNode ? stringsNode.textContent : '{}'); } catch (error) {}
    const message = key => strings[key] || key;
    const root = () => window.Shopify.routes.root;
    const validCart = cart => cart && Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count));
    const variant = item => String(item.variant_id || item.id || '');
    const plan = item => String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
    const properties = item => Array.isArray(item.properties) ? Object.fromEntries(item.properties.filter(pair => Array.isArray(pair) && pair.length === 2)) : (item.properties && typeof item.properties === 'object' ? item.properties : {});
    const identity = item => JSON.stringify([variant(item), plan(item), Object.entries(properties(item)).filter(([, value]) => value !== null && value !== '').sort(([a], [b]) => a.localeCompare(b))]);
    const related = item => !!(item.parent_relationship || item.parent_id || item.parent_line_key || item.has_components || item.item_components?.length);
    const matches = (cart, item) => cart.items.filter(line => identity(line) === identity(item));
    const total = (cart, item) => matches(cart, item).reduce((sum, line) => sum + Number(line.quantity), 0);
    const find = (cart, key, snapshot) => {
        const exact = cart.items.find(item => item.key === key);
        if (exact && (!snapshot || identity(exact) === identity(snapshot))) return exact;
        const candidates = snapshot ? matches(cart, snapshot) : [];
        return candidates.length === 1 ? candidates[0] : null;
    };
    const result = (status, cart, extra = {}) => ({ status, cart, sections: cart?.sections || null, message: '', locked: false, ...extra });
    // Audit A7 (11 Oct 2026): a form post that carries a non-empty file (the custom photo
    // tees, up to 8MB per photo) can take minutes on a slow connection.
    const hasUpload = body => typeof FormData !== 'undefined' && body instanceof FormData && [...body.values()].some(value => value && typeof value !== 'string' && value.size > 0);
    // Audit A7: same lines, same quantities, in the same order = the cart did not change.
    const sameLines = (a, b) => Number(a.item_count) === Number(b.item_count) && a.items.length === b.items.length && a.items.every((line, index) => line.key === b.items[index].key && Number(line.quantity) === Number(b.items[index].quantity));

    async function request(path, options = {}) {
        if (options.method === 'POST') writeStarted = true;
        const controller = new AbortController();
        // Audit A7 (11 Oct 2026): 5 minutes for photo uploads (was 30 seconds for everything,
        // which aborted a slow upload mid-flight and locked the cart). timedOut records that
        // the abort came from this timer and no response had arrived, so add() can tell a
        // request we cancelled ourselves from a genuinely ambiguous failure.
        let timedOut = false;
        const upload = hasUpload(options.body);
        const timer = setTimeout(() => { timedOut = true; controller.abort(); }, upload ? 300000 : 30000);
        try {
            const response = await fetch(root() + path, { cache: 'no-store', ...options, signal: controller.signal });
            let data = null;
            try { data = await response.json(); } catch (error) {}
            return { ok: response.ok, status: response.status, data, ambiguous: response.ok ? !data : response.status >= 500 };
        } catch (error) {
            return { ok: false, data: null, ambiguous: true, timedOut, upload };
        } finally {
            clearTimeout(timer);
        }
    }

    async function read() {
        const response = await request('cart.js');
        if (!response.ok || !validCart(response.data)) throw new Error('cart_read_failed');
        lastCart = response.data;
        return response.data;
    }

    const post = (path, body) => request(path, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
    const change = (key, quantity) => post('cart/change.js', { id: key, quantity, sections: 'cart-drawer' });

    function setBusy(value) {
        busy = value;
        document.querySelectorAll('button.add-to-cart, .sticky-add-to-cart, .quantity-control button, [data-lkc-upsell-add], .freegift-add-to-cart, .discount-code-container > button, .remove-discount, .order-notes-submit, .quick-view-modal-edit-btn, .quick-view-modal-free-gift-btn').forEach(button => {
            if (value) {
                if (!disabled.has(button)) disabled.set(button, button.disabled);
                button.disabled = true;
            }
        });
        if (!value && !unresolved) {
            disabled.forEach((previous, button) => { if (button.isConnected) button.disabled = previous; });
            disabled.clear();
        }
        const checkout = document.querySelector('#cart-drawer > .cart-footer > #checkout');
        if (checkout) checkout.disabled = value || unresolved || Number(document.querySelector('#cart-drawer-item-count .count-label')?.textContent || 0) === 0;
        const drawer = document.getElementById('cart-drawer');
        if (drawer) drawer.setAttribute('aria-busy', String(value));
    }

    async function run(operation) {
        if (unresolved) return result('uncertain', lastCart, { message: message('uncertain'), locked: true });
        if (busy) return result('busy', lastCart, { message: message('busy') });
        const currentSequence = ++sequence;
        writeStarted = false;
        setBusy(true);
        let answer;
        try {
            answer = await operation();
        } catch (error) {
            answer = writeStarted ? uncertain(lastCart) : result('rejected', lastCart, { message: message('read_failed') });
        } finally {
            if (answer?.locked) unresolved = true;
            if (validCart(answer?.cart)) lastCart = answer.cart;
            setBusy(false);
        }
        answer.sequence = currentSequence;
        return answer;
    }

    async function latest() {
        try { return await read(); } catch (error) { return null; }
    }

    function uncertain(cart) {
        return result('uncertain', cart, { message: message('uncertain'), locked: true });
    }

    function addItem(form) {
        if (form instanceof FormData) {
            const item = { id: form.get('id'), quantity: Number(form.get('quantity') || 1), properties: {} };
            for (const [name, value] of form.entries()) {
                const match = /^properties\[(.*)\]$/.exec(name);
                if (match) item.properties[match[1]] = typeof value === 'string' ? value : value.name;
            }
            if (form.get('selling_plan')) item.selling_plan = form.get('selling_plan');
            return item;
        }
        return form.items ? form.items[0] : form;
    }

    async function add(form, options = {}) {
        return run(async () => {
            const before = await read();
            const item = addItem(form);
            if (!item.id || !Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 1) return result('rejected', before, { message: message('invalid_quantity') });
            if (options.onlyIfMissing && before.items.some(line => variant(line) === String(item.id) || (options.productId && String(line.product_id) === String(options.productId)))) return result('success', before);
            const response = form instanceof FormData
                ? await request('cart/add.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: form })
                : await post('cart/add.js', { items: [item], sections: 'cart-drawer' });
            if (response.ok && response.data) {
                const added = response.data.items ? response.data.items[0] : response.data;
                if (added && variant(added) === String(item.id) && Number(added.quantity) > 0) {
                    return result('success', null, { sections: response.data.sections || null, added });
                }
            }
            const cart = await latest();
            if (!cart) return uncertain(null);
            const delta = total(cart, item) - total(before, item);
            if (delta === Number(item.quantity)) return result('recovered', cart);
            if (delta > 0 && delta < Number(item.quantity) && !response.ambiguous) {
                return result('partial', cart, { message: response.data?.description || response.data?.message || message('partial_add') });
            }
            if (delta === 0 && !response.ambiguous) return result('rejected', cart, { message: response.data?.description || response.data?.message || message('add_failed') });
            // Audit A7 (11 Oct 2026): we cancelled the request ourselves (timer) before any
            // response arrived, and the cart read back is line-for-line what it was before:
            // nothing was added, so let the customer try again instead of locking the cart.
            // sameLines is used rather than delta because an uploaded photo's property is a
            // file name here and a URL in the cart, so delta is always 0 for those items.
            // Anything else that is ambiguous still locks below.
            // Limited to photo uploads: other adds keep the original lock on an ambiguous outcome.
            if (response.timedOut && response.upload && sameLines(before, cart)) return result('rejected', cart, { message: message('update_failed') });
            return uncertain(cart);
        });
    }

    async function updateQuantity(before, item, quantity) {
        const response = await change(item.key, quantity);
        const cart = response.ok && validCart(response.data) ? response.data : await latest();
        if (!cart) return uncertain(null);
        const current = find(cart, item.key, item);
        if (total(cart, item) === total(before, item) - Number(item.quantity) + quantity) {
            return result(response.ok ? 'success' : 'recovered', cart);
        }
        if (response.ok && current && Number.isFinite(Number(current.quantity))) {
            return result('partial', cart, { message: message('quantity_adjusted'), confirmedQuantity: Number(current.quantity) });
        }
        if (!response.ambiguous && current && Number(current.quantity) === Number(item.quantity)) {
            return result('rejected', cart, { message: response.data?.description || response.data?.message || message('change_failed') });
        }
        return uncertain(cart);
    }

    async function quantity(key, quantity, snapshot) {
        return run(async () => {
            const before = await read();
            const item = find(before, key, snapshot);
            if (!item || (snapshot && Number(item.quantity) !== Number(snapshot.quantity))) return result('rejected', before, { message: message('changed_elsewhere') });
            if (!Number.isInteger(quantity) || quantity < 0) return result('rejected', before, { message: message('invalid_quantity') });
            if (Number(item.quantity) === quantity) return result('success', before);
            return updateQuantity(before, item, quantity);
        });
    }

    async function update(fields) {
        return run(async () => {
            const before = await read();
            const payload = { ...fields, sections: 'cart-drawer' };
            if (fields.discount) {
                const codes = [...discountCodes(before), ...String(fields.discount).split(',').map(code => code.trim()).filter(Boolean)];
                payload.discount = [...new Map(codes.map(code => [code.toLowerCase(), code])).values()].join(',');
            }
            const response = await post('cart/update.js', payload);
            if (response.ok && validCart(response.data)) return result('success', response.data);
            const cart = await latest();
            if (!cart) return uncertain(null);
            if (Object.hasOwn(fields, 'note') && String(cart.note || '') === String(fields.note || '')) return result('recovered', cart);
            if (!response.ambiguous) return result('rejected', cart, { message: response.data?.description || response.data?.message || message('update_failed') });
            return uncertain(cart);
        });
    }

    async function refresh() {
        return run(async () => result('success', await read()));
    }

    async function compensate(before, cart, original, replacement, delta, failureMessage) {
        const old = find(cart, original.key, original);
        const added = matches(cart, replacement);
        const expected = total(before, replacement) + delta;
        if (!old || Number(old.quantity) !== Number(original.quantity) || added.length !== 1 || total(cart, replacement) !== expected) return uncertain(cart);
        const target = added[0];
        const response = await change(target.key, Number(target.quantity) - delta);
        const restored = response.ok && validCart(response.data) ? response.data : await latest();
        if (!restored) return uncertain(null);
        const kept = find(restored, original.key, original);
        if (kept && Number(kept.quantity) === Number(original.quantity) && total(restored, replacement) === total(before, replacement)) {
            return result('rejected', restored, { message: failureMessage || message('edit_failed') });
        }
        return uncertain(restored);
    }

    async function edit(form, key, snapshot) {
        return run(async () => {
            const before = await read();
            const original = find(before, key, snapshot);
            if (!original || (snapshot && Number(original.quantity) !== Number(snapshot.quantity))) return result('rejected', before, { message: message('changed_elsewhere') });
            if (related(original)) return result('rejected', before, { message: message('bundle_edit') });
            const selected = addItem(form);
            const count = Number(selected.quantity);
            if (!selected.id || !Number.isInteger(count) || count < 1) return result('rejected', before, { message: message('invalid_quantity') });
            if (String(selected.id) === variant(original)) {
                if (count === Number(original.quantity)) return result('success', before);
                return updateQuantity(before, original, count);
            }
            const replacement = { id: selected.id, quantity: count, properties: { ...properties(original) } };
            if (plan(original)) replacement.selling_plan = plan(original);
            if (matches(before, replacement).length > 1) return result('rejected', before, { message: message('changed_elsewhere') });
            const response = await post('cart/add.js', { items: [replacement], sections: 'cart-drawer' });
            const cart = await latest();
            if (!cart) return uncertain(null);
            const delta = total(cart, replacement) - total(before, replacement);
            const old = find(cart, original.key, original);
            if (!old || Number(old.quantity) !== Number(original.quantity)) return uncertain(cart);
            if (delta !== count) {
                if (delta > 0 && delta < count && !response.ambiguous) return compensate(before, cart, original, replacement, delta, response.data?.description || message('edit_failed'));
                if (delta === 0 && !response.ambiguous) return result('rejected', cart, { message: response.data?.description || response.data?.message || message('edit_failed') });
                return uncertain(cart);
            }
            const removal = await change(old.key, 0);
            const finalCart = removal.ok && validCart(removal.data) ? removal.data : await latest();
            if (!finalCart) return uncertain(null);
            const remaining = total(finalCart, original);
            if (remaining === total(before, original) - Number(original.quantity) && total(finalCart, replacement) === total(before, replacement) + count) {
                return result(response.ok && removal.ok ? 'success' : 'recovered', finalCart);
            }
            if (removal.ambiguous) return uncertain(finalCart);
            return compensate(before, finalCart, original, replacement, count, removal.data?.description || message('edit_failed'));
        });
    }

    function snapshot(element) {
        try { return JSON.parse(element?.dataset.cartLine || 'null'); } catch (error) { return null; }
    }

    function discountApplied(cart, code) {
        if (!validCart(cart)) return false;
        const entered = String(code || '').trim().toLowerCase();
        if (Array.isArray(cart.discount_codes)) {
            const match = cart.discount_codes.find(item => String(item.code || '').toLowerCase() === entered);
            if (match && typeof match.applicable === 'boolean') return match.applicable;
        }
        return discountCodes(cart).some(applied => applied.toLowerCase() === entered);
    }

    function discountCodes(cart) {
        if (!validCart(cart)) return [];
        const codes = (cart.discount_codes || []).filter(item => item.applicable === true && item.code).map(item => String(item.code).trim());
        const applications = [...(cart.cart_level_discount_applications || [])];
        for (const item of cart.items) {
            for (const allocation of item.line_level_discount_allocations || []) {
                if (allocation.discount_application) applications.push(allocation.discount_application);
            }
        }
        for (const application of applications) {
            if (application.type === 'discount_code' && (application.title || application.code)) codes.push(String(application.title || application.code).trim());
        }
        return [...new Map(codes.map(code => [code.toLowerCase(), code])).values()];
    }

    function showMessage(container, answer) {
        if (!container) return;
        let banner = container.querySelector('.lkc-cart-operation-message');
        if (!answer.message && !answer.locked) {
            if (banner) banner.remove();
            return;
        }
        if (!banner) {
            banner = document.createElement('div');
            banner.className = 'lkc-cart-operation-message lkc-quickview-error';
            banner.setAttribute('role', 'alert');
            banner.style.cssText = 'color:#a32920;background:#fff3ef;border:1px solid #a32920;margin:10px 0;padding:10px;font-size:14px;';
            container.prepend(banner);
        }
        banner.replaceChildren(document.createTextNode(answer.message || message('uncertain')));
        if (answer.locked || answer.refreshFailed) {
            const link = document.createElement('a');
            link.href = root() + 'cart';
            link.textContent = message('review_cart');
            link.style.cssText = 'display:block;text-decoration:underline;margin-top:8px;';
            banner.append(link);
        }
    }

    function swap(sections, cart) {
        const section = sections && sections['cart-drawer'];
        if (typeof section !== 'string' || !section.trim()) return false;
        const doc = new DOMParser().parseFromString(section, 'text/html');
        const form = doc.getElementById('form-cart-drawer');
        const live = document.getElementById('form-cart-drawer');
        const count = doc.querySelector('#cart-drawer-item-count .count-label') || doc.getElementById('cart-drawer-item-count');
        if (!form || !live || !count || !Number.isFinite(Number(count.textContent.trim()))) return false;
        live.innerHTML = form.innerHTML;
        const countLabel = document.querySelector('#cart-drawer-item-count .count-label');
        const bubble = document.getElementById('cart-count-bubble');
        if (countLabel) countLabel.textContent = count.textContent.trim();
        if (bubble) bubble.textContent = count.textContent.trim();
        if (validCart(cart)) lastCart = cart;
        return true;
    }

    async function render(answer, options = {}) {
        if (answer.status === 'busy') return false;
        if (answer.sequence !== undefined && answer.sequence !== sequence) return false;
        let rendered = swap(answer.sections, answer.cart);
        if (!rendered && answer.status !== 'rejected') {
            if (!validCart(answer.cart)) answer.cart = await latest();
            if (answer.sequence !== undefined && answer.sequence !== sequence) return false;
            const response = await request('?sections=cart-drawer');
            if (answer.sequence !== undefined && answer.sequence !== sequence) return false;
            if (response.ok) rendered = swap(response.data, answer.cart);
        }
        if (validCart(answer.cart)) {
            const count = document.querySelector('#cart-drawer-item-count .count-label');
            const bubble = document.getElementById('cart-count-bubble');
            if (count) count.textContent = answer.cart.item_count;
            if (bubble) bubble.textContent = answer.cart.item_count;
        }
        if (!rendered && ['success', 'recovered', 'partial', 'uncertain'].includes(answer.status)) {
            answer.refreshFailed = true;
            if (!answer.message) answer.message = message('refresh_failed');
        }
        const drawer = document.getElementById('cart-drawer');
        // Audit C4 (11 Oct 2026): a rejected change made while the drawer is closed (add to
        // cart on a product page, quick view or wishlist, which show their own message) no
        // longer writes its banner into the closed drawer, where it used to greet the next
        // cart open as a stale red error. Locked / refresh-failed notices are still written,
        // and answers without a message still clear the banner, open or closed.
        if (drawer && (drawer.classList.contains('active') || answer.status !== 'rejected' || answer.locked || answer.refreshFailed)) showMessage(drawer.querySelector('.scrollable-container') || drawer, answer);
        if (options.open !== false && rendered && ['success', 'recovered', 'partial'].includes(answer.status)) {
            drawer?.classList.add('active');
            document.getElementById('cart-drawer-overlay')?.classList.add('active');
        }
        if (options.recalculate !== false && !unresolved && ['success', 'recovered', 'partial'].includes(answer.status)) {
            try { theme.PromotionalProgressbar?.recalculate(answer.cart); } catch (error) {}
        }
        if (rendered) {
            try { theme.CartDrawerUpsell?.load(); } catch (error) {}
            try { theme.QuickView?.load(); } catch (error) {}
        }
        setBusy(busy);
        return rendered;
    }

    return { add, edit, quantity, update, refresh, render, message: showMessage, snapshot, identity, discountApplied, blocked: () => busy || unresolved, get busy() { return busy; } };
})();
